import { useEffect } from 'react';
import { addSchemaToHead } from '@/utils/schemaMarkup';
import { useSEOSettings } from '@/providers/SEOProvider';
import { getAssignedVariant, trackAbView, type Variant } from '@/utils/abTesting';

interface SEOConfig {
	title?: string;
	description?: string;
	keywords?: string;
	ogTitle?: string;
	ogDescription?: string;
	ogImage?: string;
	canonicalUrl?: string;
	noIndex?: boolean;
	ogType?: string;
}

interface UseSEOOptions extends SEOConfig {
	titleTemplate?: string;
	additionalSchema?: object;
	abTest?: {
		page: string;
		variants: { A: Partial<SEOConfig>; B: Partial<SEOConfig> };
	};
}

/**
 * Default SEO values for the Florida Highwaymen Auction site
 */
const DEFAULT_SEO = {
	titleTemplate: '{title} | Florida Highwaymen Auction - Authentic Paintings & Art',
	siteName: 'Florida Highwaymen Auction',
	baseDescription: 'Premier auction house specializing in authentic Florida Highwaymen paintings and fine art by renowned African American landscape artists. Expert authentication, appraisals & auctions.',
	baseUrl: 'https://floridahighwaymenauction.com',
	defaultImage: '/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png'
};

/**
 * Update document meta tag
 */
const updateMetaTag = (name: string, content: string, property = false): void => {
	const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
	let element = document.querySelector(selector) as HTMLMetaElement;
	
	if (!element) {
		element = document.createElement('meta');
		if (property) {
			element.setAttribute('property', name);
		} else {
			element.setAttribute('name', name);
		}
		document.head.appendChild(element);
	}
	
	element.setAttribute('content', content);
};

/**
 * Update canonical URL
 */
const updateCanonicalUrl = (url: string): void => {
	let element = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
	
	if (!element) {
		element = document.createElement('link');
		element.setAttribute('rel', 'canonical');
		document.head.appendChild(element);
	}
	
	element.setAttribute('href', url);
};

/**
 * Add structured data (JSON-LD) to the page
 */
const addStructuredData = (data: object): void => {
	// Remove existing structured data with same @type
	const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
	const dataType = (data as any)['@type'];
	
	existingScripts.forEach(script => {
		try {
			const existingData = JSON.parse(script.textContent || '{}');
			if (existingData['@type'] === dataType) {
				script.remove();
			}
		} catch (e) {
			// Invalid JSON, remove it
			script.remove();
		}
	});
	
	// Add new structured data
	const script = document.createElement('script');
	script.type = 'application/ld+json';
	script.textContent = JSON.stringify(data);
	document.head.appendChild(script);
};

/**
 * Custom hook for managing SEO meta tags and structured data
 */
export const useSEO = (options: UseSEOOptions = {}) => {
	const { settings } = useSEOSettings();
	useEffect(() => {
		let resolvedOptions = { ...options } as UseSEOOptions;
		// Optional A/B testing
		if (options.abTest) {
			const variant: Variant = getAssignedVariant();
			const patch = options.abTest.variants[variant];
			resolvedOptions = { ...resolvedOptions, ...patch };
			trackAbView(options.abTest.page, variant);
		}

		const {
			title,
			description,
			keywords,
			ogTitle,
			ogDescription,
			ogImage,
			canonicalUrl,
			noIndex = false,
			titleTemplate = settings?.default_meta_title_template || DEFAULT_SEO.titleTemplate,
			additionalSchema,
			ogType = 'website'
		} = resolvedOptions;

		const baseUrl = settings?.canonical_domain || DEFAULT_SEO.baseUrl;
		const siteName = settings?.site_title || DEFAULT_SEO.siteName;
		const resolvedOgImage = ogImage ? (ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`) : `${baseUrl}${DEFAULT_SEO.defaultImage}`;

		// Update document title
		if (title) {
			const formattedTitle = titleTemplate.replace('{title}', title).replace('{site_title}', siteName);
			document.title = formattedTitle;
			updateMetaTag('og:title', ogTitle || formattedTitle, true);
		}

		// Update meta description
		if (description) {
			updateMetaTag('description', description);
			updateMetaTag('og:description', ogDescription || description, true);
		} else if (settings?.default_meta_description_template) {
			const templated = settings.default_meta_description_template.replace('{site_title}', siteName);
			updateMetaTag('description', templated);
			updateMetaTag('og:description', templated, true);
		}

		// Update keywords
		if (keywords) {
			updateMetaTag('keywords', keywords);
		}

		// OG type and site name
		updateMetaTag('og:type', ogType, true);
		updateMetaTag('og:site_name', siteName, true);

		// Update og:image for Facebook sharing
		updateMetaTag('og:image', resolvedOgImage, true);
		updateMetaTag('og:image:width', '1200', true);
		updateMetaTag('og:image:height', '630', true);
		updateMetaTag('og:image:alt', `${title || siteName} - Florida Highwaymen Art`, true);

		// Update canonical URL and og:url
		if (canonicalUrl) {
			const fullUrl = canonicalUrl.startsWith('http') ? canonicalUrl : `${baseUrl}${canonicalUrl}`;
			updateCanonicalUrl(fullUrl);
			updateMetaTag('og:url', fullUrl, true);
		}

		// Handle noIndex
		if (noIndex) {
			updateMetaTag('robots', 'noindex, nofollow');
		} else {
			updateMetaTag('robots', 'index, follow');
		}

		// Add basic organization structured data if enabled
		if (settings?.schema_org_enabled !== false) {
			const organizationData = {
				"@context": "https://schema.org",
				"@type": "Organization",
				"name": siteName,
				"url": baseUrl,
				"logo": `${baseUrl}${DEFAULT_SEO.defaultImage}`,
				"description": settings?.site_description || DEFAULT_SEO.baseDescription,
				"address": {
					"@type": "PostalAddress",
					"streetAddress": "8214 Griffin Rd",
					"addressLocality": "Davie",
					"addressRegion": "FL",
					"postalCode": "33328",
					"addressCountry": "US"
				},
				"contactPoint": {
					"@type": "ContactPoint",
					"telephone": "+1-954-605-0748",
					"contactType": "customer service",
					"email": "info@floridahighwaymenauction.com"
				}
			};
			addStructuredData(organizationData);
		}

		// Add additional schema markup if provided
		if (additionalSchema) {
			addStructuredData(additionalSchema);
		}

	}, [options, settings]);

	return {
		updateTitle: (newTitle: string) => {
			const siteName = settings?.site_title || DEFAULT_SEO.siteName;
			const formattedTitle = (options.titleTemplate || settings?.default_meta_title_template || DEFAULT_SEO.titleTemplate)
				.replace('{title}', newTitle)
				.replace('{site_title}', siteName);
			document.title = formattedTitle;
			updateMetaTag('og:title', formattedTitle, true);
		},
		updateDescription: (newDescription: string) => {
			updateMetaTag('description', newDescription);
			updateMetaTag('og:description', newDescription, true);
		},
		addStructuredData
	};
};

/**
 * Predefined SEO configurations for common page types
 */
export const SEO_CONFIGS = {
	homepage: {
		title: 'Florida Highwaymen Paintings - Authentic Art Auction',
		description: 'Premier Florida Highwaymen auction featuring authentic paintings by legendary African American landscape artists. Buy original art, expert authentication, appraisals & live auctions.',
		keywords: 'Florida Highwaymen, Florida Highwaymen paintings, Florida Highwaymen art, buy Florida Highwaymen, Florida Highwaymen auction, authentic Florida Highwaymen, African American art'
	},
	
	auctions: {
		title: 'Florida Highwaymen Art Auctions - Live Bidding',
		description: 'Browse live Florida Highwaymen auctions featuring authentic paintings. Bid on original artworks by legendary landscape artists. Current and upcoming auction lots.',
		keywords: 'Florida Highwaymen auction, art auction, live bidding, Florida Highwaymen for sale, buy Florida Highwaymen paintings, art collecting'
	},
	
	artworks: {
		title: 'Florida Highwaymen Paintings - Browse Authentic Art',
		description: 'Explore authentic Florida Highwaymen paintings for sale. View high-resolution images, provenance details, and auction history of original artworks by legendary artists.',
		keywords: 'Florida Highwaymen paintings, buy Florida Highwaymen art, Florida Highwaymen for sale, authentic Florida Highwaymen, landscape paintings, African American art'
	},
	
	artists: {
		title: 'Florida Highwaymen Artists - Complete Directory',
		description: 'Complete directory of Florida Highwaymen artists with biographies, signature styles, and artworks. Learn about the 26 original legendary African American landscape painters.',
		keywords: 'Florida Highwaymen artists, Florida Highwaymen painters, African American artists, landscape artists, original Florida Highwaymen, art history'
	},
	
	blog: {
		title: 'Florida Highwaymen Blog - Art History & Collecting',
		description: 'Expert articles about Florida Highwaymen art history, collecting tips, artist profiles, and authentication guides. Educational content from certified art experts.',
		keywords: 'Florida Highwaymen history, art collecting tips, Florida Highwaymen authentication, art education, art investment, collecting guide'
	},
	
	contact: {
		title: 'Contact Florida Highwaymen Auction - Expert Consultation',
		description: 'Contact Florida Highwaymen Auction for consignment, appraisals, authentication, or auction inquiries. Expert consultation available for collectors and sellers.',
		keywords: 'Florida Highwaymen contact, art appraisal, consignment inquiries, auction consultation, authentication services'
	},
	
	appraisal: {
		title: 'Florida Highwaymen Art Appraisal - Expert Valuation',
		description: 'Professional Florida Highwaymen artwork appraisal and authentication services. Certified experts provide accurate valuations for insurance, estate, or sale purposes.',
		keywords: 'Florida Highwaymen appraisal, art appraisal, artwork valuation, Florida Highwaymen authentication, art evaluation, certified appraisers'
	},
	
	consign: {
		title: 'Consign Florida Highwaymen Art - Sell Your Paintings',
		description: 'Consign your Florida Highwaymen artwork through our premier auction platform. Expert marketing, authentication, and maximum exposure to serious collectors worldwide.',
		keywords: 'consign Florida Highwaymen, sell Florida Highwaymen art, art consignment, auction consignment, sell paintings, art sales'
	}
};

export default useSEO; 